import { useState } from 'react';
import { Modal, Button, Group, Stack, Text, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { StableKey } from 'app/models/common-types'
import { SuccessNotification, ErrorNotification } from 'app/views/components/notifications/notification';
import { useDispatch } from 'react-redux';
import { ReactComponent as CloseIcon } from 'app/public/DrawerIcons/close-icon.svg'
import { cancelApprovedMasterTrade } from 'app/state/ducks/master-trades/thunks';



interface Props {
    openTrade?: any | null;
    opened: boolean;
    close: () => void;
    openTradeId: StableKey;
}

interface CancelFormValues {
    reason: string;
}

function CancelOpenTradeModal({ openTrade, opened, close, openTradeId }: Props) {
    const dispatch = useDispatch();
    const [isLoading, setIsLoading] = useState(false);

    const form = useForm<CancelFormValues>({
        initialValues: {
            reason: '',
        },
        validate: {
            reason: (value) => (value.trim().length > 0 ? null : 'Reason is required'),
        },
    });

    const handleSubmit = async (values: CancelFormValues) => {
        setIsLoading(true);
        try {
            const result: any = await dispatch(cancelApprovedMasterTrade(openTradeId, values.reason));
            if (result.success) {
                SuccessNotification({
                    title: 'Successful Trade Cancellation',
                    message: `Successfully canceled trade with ID: ${openTrade?.systemId ?? 'unknown'}`,
                });
                close();
            } else {
                ErrorNotification({
                    title: 'Trade Cancellation Failed',
                    message: result.payload || 'Failed to cancel trade',
                });
            }
        } catch (error) {
            console.error('Error canceling trade:', error);
            ErrorNotification({
                title: 'Trade Cancellation Error',
                message: 'An unexpected error occurred while canceling the trade',
            });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            <Modal
                opened={opened}
                onClose={close}
                centered
                withCloseButton={false}
                size="40%"
            >
                <Stack w="100%" p={40} spacing={20}>
                    <div className="cancel-open-trade-header">
                        <Text className="cancel-open-trade-title">Cancel Trade</Text>
                        <Button
                            onClick={close}
                            variant="subtle"
                            style={{
                                padding: 0,
                                background: 'transparent',
                                height: 30,
                                width: 30,
                                minWidth: 'auto',
                            }}
                            aria-label="Close modal"
                        >
                            <CloseIcon style={{ width: '30px' }} />
                        </Button>
                    </div>
                    <div className="cancel-open-trade-content">
                        <div className="cancel-open-trade-text-container">
                            <div className="cancel-open-trade-text-frame">
                                <Text className="cancel-open-trade-text">
                                    Are you sure you want to cancel Trade {openTrade?.systemId ?? ''}?
                                </Text>
                                <Text className="cancel-open-trade-text">
                                    If so, please enter a reason below and click Yes
                                </Text>
                            </div>
                        </div>
                        <form style={{ width: '100%' }} onSubmit={form.onSubmit(handleSubmit)}>
                            <div style={{ display: "flex", flexDirection: "column", width: '100%' }}>
                                <div className="cancel-open-trade-label-wrapper">
                                    <Text className="cancel-open-trade-label">Reason:</Text>
                                </div>

                                <TextInput
                                    placeholder="Enter a reason for trade cancellation"
                                    width="100%"
                                    className="cancel-open-trade-input newInputWrapper newInput"
                                    classNames={{ input: 'reasonInputText' }}
                                    {...form.getInputProps('reason')}
                                />
                                {form.errors.reason && (
                                    <Text color="red" size="sm" mt={4}>
                                        {form.errors.reason}
                                    </Text>
                                )}
                                <Group position="right" mt="md">
                                    <Button className='cancel-open-trade-cancel-button' onClick={close}>
                                        <div className="cancel-open-trade-cancel-button-text"> Cancel </div>
                                    </Button>
                                    <Button type="submit" className='cancel-open-trade-yes-button' disabled={isLoading || !form.isValid()}><div className='cancel-open-trade-yes-button-text'>{isLoading ? 'Processing...' : 'Yes'}</div></Button>
                                </Group>
                            </div>
                        </form>

                    </div>
                </Stack>
            </Modal >
        </>

    );
}

export default CancelOpenTradeModal;